import type { ComponentProps, ReactNode } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { Sheet, SheetContent } from '@nesso/ui/primitives/sheet';

import { type SheetProps as SheetData, useSheetStore } from '@/shared/stores/sheet-store';

type Props = {
    type: SheetData['type'];
    children: ReactNode;
    side?: ComponentProps<typeof SheetContent>['side'];
    size?: ComponentProps<typeof SheetContent>['size'];
    variant?: ComponentProps<typeof SheetContent>['variant'];
    className?: string;
    showCloseButton?: ComponentProps<typeof SheetContent>['showCloseButton'];
    onRequestClose?: () => void;
    onOpenAutoFocus?: (e: Event) => void;
    onInteractOutside?: ComponentProps<typeof SheetContent>['onInteractOutside'];
    onPointerDownOutside?: ComponentProps<typeof SheetContent>['onPointerDownOutside'];
    onEscapeKeyDown?: ComponentProps<typeof SheetContent>['onEscapeKeyDown'];
};

export function SheetPanel({
    type,
    children,
    side,
    size,
    variant,
    className,
    showCloseButton,
    onRequestClose,
    onOpenAutoFocus,
    onInteractOutside,
    onPointerDownOutside,
    onEscapeKeyDown,
}: Props) {
    const { currentSheet, closeAllSheets } = useSheetStore(
        useShallow((state) => ({
            currentSheet: state.currentSheet,
            closeAllSheets: state.closeAllSheets,
        })),
    );

    const isOpen = currentSheet?.type === type;

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            if (onRequestClose) {
                onRequestClose();
            } else {
                closeAllSheets();
            }
        }
    };

    const handleInteractOutside: ComponentProps<typeof SheetContent>['onInteractOutside'] = (
        event,
    ) => {
        // Touch dismissal lands after the stacked alert dialog closes, so Cancel would reopen the prompt.
        if ((event.target as Element | null)?.closest('[role="alertdialog"]')) {
            event.preventDefault();
        }
        onInteractOutside?.(event);
    };

    return (
        <Sheet open={isOpen} onOpenChange={handleOpenChange}>
            <SheetContent
                side={side}
                size={size}
                variant={variant}
                className={className}
                onOpenAutoFocus={onOpenAutoFocus}
                onInteractOutside={handleInteractOutside}
                onPointerDownOutside={onPointerDownOutside}
                onEscapeKeyDown={onEscapeKeyDown}
                showCloseButton={showCloseButton}
            >
                {children}
            </SheetContent>
        </Sheet>
    );
}

export {
    SheetBackButton,
    SheetBody,
    SheetClose,
    SheetCloseButton,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '@nesso/ui/primitives/sheet';
